import { useContext, useEffect, useState } from "react"
import { ProjectContext } from "../App"
import { AxiosResponse } from "axios"
import { Invoice } from "../types/invoice.type"
import { httpGet } from "../services/Http.service"
import { apiUrl } from "../configs/config.constants"
import { ShortUrl } from "../types/short-url.type"
import { ServiceBooking } from "../types/service.type"
import { Order } from "../types/order.type"
import { Rental } from "../types/rental.type"
import { mapLeaseToRental, type LeasePublicResponse } from "../utils/mapLeaseToRental"

export function useGetInvoice(
  shortUrlCode: string
) {
  const { project } = useContext(ProjectContext)
  const [shortUrl, setShortUrl] = useState<ShortUrl | null>(null)
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [booking, setBooking] = useState<ServiceBooking | null>(null)
  const [loading, setLoading] = useState(false)
  const [order, setOrder] = useState<Order | null>(null)
  const [rental, setRental] = useState<Rental | null>(null)

  useEffect(() => {
    if (project?.projectId) {
      getShortUrl(shortUrlCode)
    }
  }, [project, shortUrlCode])

  useEffect(() => {
    if (shortUrl?.code) {
      getInvoice(shortUrl.refId)
    }
  }, [shortUrl])

  useEffect(() => {
    if (invoice?.sourceId && invoice.sourceType === "booking") {
      getBooking(invoice.sourceId)
    }

    if (invoice?.sourceId && invoice.sourceType === "order") {
      getOrder(invoice.sourceId)
    }

    if (invoice?.sourceId && invoice.sourceType === "rental") {
      getRental(invoice.sourceId)
    }
  }, [invoice])

  function getShortUrl(code: string) {
    setLoading(true)
    httpGet(apiUrl.shortUrl.get(code))
      .then((response: AxiosResponse<{ data: ShortUrl }>) => {
        setShortUrl(response.data.data)
      })
  }

  function getInvoice(invoiceId: string) {
    httpGet(apiUrl.invoice.get(invoiceId))
      .then((response: AxiosResponse<Invoice>) => {
        setInvoice(response.data)
      })
      .catch((error: any) => {
        console.error(error)
      })
  }

  function getBooking(bookingId: string) {
    httpGet(apiUrl.booking.get(bookingId))
      .then((response: AxiosResponse<ServiceBooking>) => {
        setBooking(response.data)
      })
      .catch((error: any) => {
        console.error(error)
      }).finally(() => {
        setLoading(false)
      })
  }

  function getOrder(orderId: string) {
    httpGet(apiUrl.order.get(orderId))
      .then((response: AxiosResponse<Order>) => {
        setOrder(response.data)
      })
      .catch((error: any) => {
        console.error(error)
      }).finally(() => {
        setLoading(false)
      })
  }

  function getRental(leaseId: string) {
    httpGet(apiUrl.rental.get(leaseId))
      .then((response: AxiosResponse<LeasePublicResponse>) => {
        setRental(mapLeaseToRental(response.data))
      })
      .catch((error: any) => {
        console.error(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return {
    invoice,
    booking,
    order,
    rental,
    loading,
  }
}